import React, {PropsWithChildren, useEffect, useState} from "react";
import {useTranslation} from "@app/component/translation/Translation";
import has from "lodash/has";
import get from "lodash/get";
import clsx from "clsx";
import {Controller} from "react-hook-form";
import {
    clearIndicatorStyles,
    controlStyles,
    dropdownIndicatorStyles,
    groupHeadingStyles,
    indicatorsContainerStyles,
    indicatorSeparatorStyles,
    menuStyles,
    multiValueLabelStyles,
    multiValueRemoveStyles,
    multiValueStyles,
    noOptionsMessageStyles,
    optionStyles,
    placeholderStyles,
    selectInputStyles,
    singleValueStyles,
    valueContainerStyles
} from "@app/component/form/input/autocomplete/AutocompleteStyles";
import Select from "react-select";
import Country from "@app/model/hotel/Country";
import isEmpty from "lodash/isEmpty";
import {useQuery} from "@tanstack/react-query";
import RefDataService from "@app/model/refdata/service/RefDataService";
import {CountriesQueryKeys} from "@app/model/refdata/service/RefDataQueryKeys";


type Props = {
    form: any,
    name: string,
    label?: string,
    placeholder?: string,
    isRequired?: boolean
}
const CountryAutocompleteField:
    React.FunctionComponent<PropsWithChildren<Props>> = ({
                                                             children,
                                                             name,
                                                             label,
                                                             placeholder,
                                                             form,
                                                             isRequired = false
                                                         }) => {

    const {t} = useTranslation("common.form.error");

    const hasError = !!form && has(form.formState.errors, name);
    const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t("required")) : '';

    const {data} = useQuery({
        queryFn: RefDataService.findCountries,
        queryKey: CountriesQueryKeys.all
    })

    const [country, setCountry] = useState<Country>();

    const value = form.watch(name);

    useEffect(() => {
        if (!value) setCountry(undefined)
        else if (!isEmpty(data)) {
            setCountry(data!.find(o => o.id === value))
        }
    }, [value, data])

    const formatOptionLabel = (country: Country) => (
        <div style={{display: "flex"}}>
            <div>{country.name}</div>
        </div>
    );

    return <div>
        {!!label && <div className="label">Country</div>}
        <div className='mt-0.5'>
            <Controller name={name}
                        control={form.control}
                        rules={{required: isRequired}}
                        render={() => <Select placeholder={!!placeholder ? placeholder : ''}
                                              unstyled
                                              getOptionValue={(o: Country) => '' + o.id}
                                              getOptionLabel={(o: Country) => o.name}
                                              formatOptionLabel={formatOptionLabel}
                                              options={data}
                                              value={country}
                                              menuPortalTarget={document.body}
                                              onChange={(v: Country) => {
                                                  form.setValue(name, v.id);
                                              }}
                                              styles={{
                                                  input: (base) => ({
                                                      ...base,
                                                      'input:focus': {
                                                          boxShadow: 'none',
                                                      },
                                                  }),
                                              }}
                                              classNames={{
                                                  control: ({isFocused}) =>
                                                      clsx(
                                                          isFocused ? controlStyles(hasError).focus : controlStyles(hasError).nonFocus,
                                                          controlStyles(hasError).base,
                                                      ),
                                                  placeholder: () => placeholderStyles(hasError),
                                                  input: () => selectInputStyles,
                                                  valueContainer: () => valueContainerStyles,
                                                  singleValue: () => singleValueStyles,
                                                  multiValue: () => multiValueStyles,
                                                  multiValueLabel: () => multiValueLabelStyles,
                                                  multiValueRemove: () => multiValueRemoveStyles,
                                                  indicatorsContainer: () => indicatorsContainerStyles,
                                                  clearIndicator: () => clearIndicatorStyles,
                                                  indicatorSeparator: () => indicatorSeparatorStyles,
                                                  dropdownIndicator: () => dropdownIndicatorStyles,
                                                  menu: () => menuStyles,
                                                  groupHeading: () => groupHeadingStyles,
                                                  option: ({isFocused, isSelected}) =>
                                                      clsx(
                                                          isFocused && optionStyles.focus,
                                                          isSelected && optionStyles.selected,
                                                          optionStyles.base,
                                                      ),
                                                  noOptionsMessage: () => noOptionsMessageStyles,
                                              }}
                        />}/>

        </div>
        {!!errorMessage && <p className="help help-error">{errorMessage}</p>}
    </div>
}


export default CountryAutocompleteField;